import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = 'January 15, 2026';
  const effectiveDate = 'January 15, 2026';

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-2xl mb-6">
            <DocumentTextIcon className="w-8 h-8 text-primary-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-dark-400">
            Last Updated: {lastUpdated} | Effective Date: {effectiveDate}
          </p>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-8"
        >
          <div className="flex items-start space-x-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-400 mb-2">IMPORTANT LEGAL NOTICE</h3>
              <p className="text-sm text-dark-300">
                BY ACCESSING OR USING TEMPLUM DAO PLATFORM, YOU AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
                IF YOU DO NOT AGREE TO ALL OF THESE TERMS, DO NOT USE THIS PLATFORM. THESE TERMS INCLUDE
                A BINDING ARBITRATION CLAUSE AND CLASS ACTION WAIVER.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-8 space-y-8"
        >
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">1</span>
              Acceptance of Terms
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your")
                and NARDIHA Holdings S.L. ("Company", "we", "us", or "our"), a company incorporated under the laws of Spain,
                operating the TEMPLUM DAO platform ("Platform").
              </p>
              <p>
                By accessing, browsing, or using the Platform, including but not limited to participating in auctions,
                staking tokens, voting on governance proposals, or using any associated services, you acknowledge that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You have read, understood, and agree to be bound by these Terms</li>
                <li>You are at least 18 years of age or the age of legal majority in your jurisdiction</li>
                <li>You have the legal capacity to enter into binding contracts</li>
                <li>You are not a resident of any jurisdiction where use of the Platform is prohibited</li>
                <li>You are not on any sanctions lists maintained by the UN, EU, US, or other relevant authorities</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">2</span>
              Platform Description and Services
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                TEMPLUM DAO is a decentralized NFT auction platform that facilitates:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>NFT Auctions:</strong> Including English, Dutch, Vickrey (sealed-bid), and Candle auction formats</li>
                <li><strong>Token Staking:</strong> Staking of NDH tokens for rewards and platform benefits</li>
                <li><strong>DAO Governance:</strong> Decentralized decision-making through proposal voting</li>
                <li><strong>Fractional Ownership:</strong> Co-ownership of high-value NFT assets</li>
              </ul>
              <p className="font-semibold text-amber-400">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">3</span>
              User Eligibility and Restrictions
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <h3 className="font-semibold text-white">3.1 Eligibility Requirements</h3>
              <p>You represent and warrant that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are at least 18 years old or the age of majority in your jurisdiction</li>
                <li>You have full legal capacity to enter into these Terms</li>
                <li>You are not located in, under the control of, or a national or resident of any Restricted Territory</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">3.2 Restricted Territories</h3>
              <p>
                The Platform is NOT available to persons or entities located in, organized in, or residents of:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>United States of America and its territories</li>
                <li>People's Republic of China (mainland)</li>
                <li>Democratic People's Republic of Korea (North Korea)</li>
                <li>Iran, Syria, Cuba, Crimea region</li>
                <li>Any jurisdiction where cryptocurrency or NFT transactions are prohibited</li>
                <li>Any OFAC, UN, or EU sanctioned country or territory</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">3.3 Account Responsibility</h3>
              <p>
                You are solely responsible for maintaining the confidentiality of your wallet credentials
                and for all activities that occur through your account. We are NOT responsible for any
                loss arising from unauthorized access to your wallet.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">4</span>
              Auction Terms and Conditions
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <h3 className="font-semibold text-white">4.1 Binding Nature of Bids</h3>
              <p>
                ALL BIDS PLACED ON THE PLATFORM ARE BINDING AND IRREVOCABLE. By placing a bid, you agree to
                purchase the NFT at the bid amount if your bid is the winning bid. Failure to complete a
                winning auction may result in account suspension and forfeiture of any deposits.
              </p>

              <h3 className="font-semibold text-white mt-6">4.2 Auction Types</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>English Auction:</strong> Ascending price auction. Highest bid at end time wins.</li>
                <li><strong>Dutch Auction:</strong> Descending price auction. First acceptable bid wins.</li>
                <li><strong>Vickrey Auction:</strong> Sealed-bid auction. Winner pays second-highest bid price.</li>
                <li><strong>Candle Auction:</strong> Random end time determined by VRF. Highest bid at random end wins.</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">4.3 Platform Fees</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Standard platform fee: 2.5% of final sale price</li>
                <li>NDH token holders may receive reduced or zero fees based on tier</li>
                <li>Creator royalties: As specified by NFT contract (typically 2.5-10%)</li>
                <li>Blockchain gas fees are the responsibility of the user</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">4.4 Settlement and Delivery</h3>
              <p>
                Upon successful auction completion, the NFT will be transferred to the winner's wallet
                automatically via smart contract. The Company is NOT responsible for delays or failures
                in delivery caused by blockchain congestion, smart contract errors, or force majeure events.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">5</span>
              NDH Token and Staking
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <h3 className="font-semibold text-white">5.1 Token Nature</h3>
              <p>
                NDH tokens are utility tokens that provide access to Platform features. NDH tokens are
                NOT securities, shares, or ownership interests in NARDIHA Holdings S.L. or any affiliated entity.
                NDH tokens do NOT confer any rights to dividends, profits, or voting rights in the Company.
              </p>

              <h3 className="font-semibold text-white mt-6">5.2 Staking Risks</h3>
              <p>
                Staking NDH tokens involves risks including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Smart contract vulnerabilities or exploits</li>
                <li>Token value fluctuation</li>
                <li>Lock-up periods during which tokens cannot be withdrawn</li>
                <li>Slashing penalties for governance violations</li>
                <li>Regulatory changes affecting token utility</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">5.3 No Guaranteed Returns</h3>
              <p className="font-semibold text-amber-400">
                STAKING REWARDS ARE NOT GUARANTEED. APY rates shown are estimates based on current conditions
                and may vary. Past performance is not indicative of future results.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">6</span>
              Intellectual Property
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <h3 className="font-semibold text-white">6.1 Platform IP</h3>
              <p>
                All intellectual property rights in the Platform, including software, designs, logos, and
                trademarks, are owned by NARDIHA Holdings S.L. or its licensors. No license is granted
                except as expressly stated in these Terms.
              </p>

              <h3 className="font-semibold text-white mt-6">6.2 NFT Ownership</h3>
              <p>
                Purchasing an NFT grants you ownership of the token on the blockchain. Unless otherwise
                specified by the creator, you receive a LIMITED LICENSE to display the associated artwork
                for personal, non-commercial use. You do NOT acquire copyright, trademark, or other
                intellectual property rights in the underlying content.
              </p>

              <h3 className="font-semibold text-white mt-6">6.3 User Content</h3>
              <p>
                By listing NFTs on the Platform, you represent and warrant that you own or have the right
                to sell such NFTs and that the NFTs do not infringe any third-party rights.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">7</span>
              Prohibited Activities
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>You agree NOT to engage in any of the following:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Wash trading, market manipulation, or artificial price inflation</li>
                <li>Money laundering, terrorist financing, or sanctions evasion</li>
                <li>Fraud, misrepresentation, or deceptive practices</li>
                <li>Hacking, exploiting vulnerabilities, or unauthorized access</li>
                <li>Listing stolen, counterfeit, or infringing NFTs</li>
                <li>Circumventing geographic restrictions or KYC requirements</li>
                <li>Using bots or automated systems without authorization</li>
                <li>Harassment, defamation, or illegal content</li>
                <li>Any activity that violates applicable laws or regulations</li>
              </ul>
              <p className="font-semibold text-red-400 mt-4">
                Violation of these prohibitions may result in immediate account termination, forfeiture
                of funds, and reporting to relevant authorities.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">8</span>
              Disclaimers and Limitation of Liability
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <h3 className="font-semibold text-white">8.1 No Warranties</h3>
              <p className="uppercase font-semibold">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND,
                WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="font-semibold text-white mt-6">8.2 Blockchain Risks</h3>
              <p>
                You acknowledge and accept the inherent risks of blockchain technology, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Irreversibility of transactions</li>
                <li>Network congestion and high gas fees</li>
                <li>Smart contract bugs or vulnerabilities</li>
                <li>Hard forks, chain reorganizations, or protocol changes</li>
                <li>Private key loss or theft</li>
                <li>Regulatory uncertainty</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">8.3 Limitation of Liability</h3>
              <p className="uppercase font-semibold text-amber-400">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NARDIHA HOLDINGS S.L. AND ITS AFFILIATES,
                DIRECTORS, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS,
                DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE PLATFORM.
              </p>
              <p className="mt-4">
                IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE GREATER OF: (A) THE FEES PAID BY YOU
                TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) €100.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">9</span>
              Indemnification
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                You agree to indemnify, defend, and hold harmless NARDIHA Holdings S.L., its affiliates,
                and their respective directors, officers, employees, and agents from and against any and
                all claims, liabilities, damages, losses, costs, and expenses (including reasonable
                attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the Platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any applicable laws or regulations</li>
                <li>Your violation of any third-party rights</li>
                <li>Any content you submit or transmit through the Platform</li>
              </ul>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">10</span>
              Dispute Resolution and Arbitration
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="font-semibold text-red-400">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR
                  RIGHT TO FILE A LAWSUIT IN COURT.
                </p>
              </div>

              <h3 className="font-semibold text-white">10.1 Binding Arbitration</h3>
              <p>
                Any dispute, controversy, or claim arising out of or relating to these Terms or the
                Platform shall be finally settled by binding arbitration administered by the Spanish
                Court of Arbitration (Corte Española de Arbitraje) in accordance with its rules.
              </p>

              <h3 className="font-semibold text-white mt-6">10.2 Arbitration Procedures</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Seat of arbitration: Madrid, Spain</li>
                <li>Language: English or Spanish (at respondent's choice)</li>
                <li>Number of arbitrators: One (1)</li>
                <li>The arbitrator's decision shall be final and binding</li>
              </ul>

              <h3 className="font-semibold text-white mt-6">10.3 Class Action Waiver</h3>
              <p className="font-semibold text-amber-400">
                YOU AND THE COMPANY AGREE THAT ANY CLAIMS WILL BE BROUGHT IN YOUR OR OUR INDIVIDUAL
                CAPACITY ONLY, AND NOT AS A CLASS MEMBER OR PARTICIPANT IN ANY CLASS, COLLECTIVE,
                OR REPRESENTATIVE ACTION.
              </p>

              <h3 className="font-semibold text-white mt-6">10.4 Exceptions</h3>
              <p>
                Either party may seek injunctive relief in any court of competent jurisdiction to
                protect intellectual property rights or prevent irreparable harm.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">11</span>
              Governing Law and Jurisdiction
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of Spain,
                without regard to its conflict of law provisions.
              </p>
              <p>
                For any matters not subject to arbitration, you consent to the exclusive jurisdiction
                of the courts of Madrid, Spain.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">12</span>
              Modifications to Terms
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                We reserve the right to modify these Terms at any time. Material changes will be
                notified via the Platform or email at least 30 days before becoming effective.
              </p>
              <p>
                Your continued use of the Platform after modifications become effective constitutes
                acceptance of the modified Terms. If you do not agree, you must stop using the Platform.
              </p>
            </div>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">13</span>
              Termination
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>
                We may suspend or terminate your access to the Platform at any time, with or without
                cause, with or without notice. Upon termination:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All licenses granted to you will terminate immediately</li>
                <li>You remain liable for all obligations incurred prior to termination</li>
                <li>Sections 6-11 of these Terms shall survive termination</li>
              </ul>
            </div>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">14</span>
              General Provisions
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and Cookie Policy,
              constitute the entire agreement between you and the Company.</p>

              <p><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions
              shall continue in full force and effect.</p>

              <p><strong>Waiver:</strong> Failure to enforce any right shall not constitute a waiver of that right.</p>

              <p><strong>Assignment:</strong> You may not assign these Terms. We may assign our rights and obligations
              without your consent.</p>

              <p><strong>Force Majeure:</strong> We shall not be liable for delays or failures due to circumstances
              beyond our reasonable control.</p>
            </div>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3 text-sm text-primary-400">15</span>
              Contact Information
            </h2>
            <div className="text-dark-300 space-y-4 pl-11">
              <p>For questions about these Terms, please contact:</p>
              <div className="bg-dark-800 rounded-lg p-4 mt-4">
                <p><strong>NARDIHA Holdings S.L.</strong></p>
                <p>Legal Department</p>
                <p>Email: legal@nardiha.com</p>
                <p>Address: [Registered Office Address]</p>
                <p>CIF: B-XXXXXXXX</p>
              </div>
            </div>
          </section>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex flex-wrap justify-center gap-4"
        >
          <Link to="/privacy" className="text-primary-400 hover:text-primary-300 transition-colors">
            Privacy Policy
          </Link>
          <span className="text-dark-600">|</span>
          <Link to="/cookies" className="text-primary-400 hover:text-primary-300 transition-colors">
            Cookie Policy
          </Link>
          <span className="text-dark-600">|</span>
          <Link to="/help" className="text-primary-400 hover:text-primary-300 transition-colors">
            Help Center
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
